import { translations } from 'locales/translations';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { UserPasswordForm } from '../Components/LoginPanel';
import { SelectLoginType } from '../Components/SSO_ON';
import { TwoFactorAuthenticationForm } from '../Components/TwoFactorAuthentication';
import {
  LoginState,
  TwoFactorAuthenticationParams,
  UserPasswordRemember,
} from '../Components/type';
import ButtonLink from 'app/components/BasicLinks/ButtonLink';
import { FormikProps } from 'formik';
import { useHistory } from 'react-router-dom';

export const useLoginState = (
  loginState: LoginState,
  showNewUser: boolean,
  showRememberMe: boolean,
  isMobile: boolean,
  handleSSOOFFSubmit: (values: UserPasswordRemember) => Promise<void>,
  handleSSO_ON_Submit: () => void,
  handleSSO_OFF_Submit: () => void,
  handle2FASubmit: (values: TwoFactorAuthenticationParams | null) => void,
  ReSend: () => void,
): [
  JSX.Element | undefined,
  JSX.Element | undefined,
  JSX.Element | undefined,
] => {
  const { t } = useTranslation();
  const history = useHistory();
  const ssooffRef = React.useRef<FormikProps<UserPasswordRemember> | null>(
    null,
  );
  const twoFARef = React.useRef<FormikProps<
    TwoFactorAuthenticationParams
  > | null>(null);
  const pageTitle = React.useMemo(() => {
    switch (loginState) {
      case 'Login_SSOOFF':
      case 'SSOON':
      case 'Login_SSOON':
        return <>{t(translations.LogInToYourAccount)}</>;
      case 'TwoFactorAuthentication':
        return <>{t(translations.TwoFactorAuthenticationIsRequired)}</>;
      default:
        return undefined;
    }
  }, [loginState, t]);
  const pageDescription = React.useMemo(() => {
    switch (loginState) {
      case 'Login_SSOOFF':
      case 'SSOON':
      case 'Login_SSOON':
        if (showNewUser) {
          return (
            <>
              {t(translations.JoinYourLaboratoryAsA)}{' '}
              <ButtonLink
                href={''}
                onClick={() => {
                  history.push('/RegisterAccount');
                }}
              >
                {t(translations.NewUser)}
              </ButtonLink>
            </>
          );
        } else return undefined;
      case 'TwoFactorAuthentication':
        return (
          <>
            {t(translations.HaventReceivedTheCode)}{' '}
            <ButtonLink onClick={ReSend}>{t(translations.ReSend)}</ButtonLink>
          </>
        );
      default:
        return undefined;
    }
  }, [ReSend, history, loginState, showNewUser, t]);
  const MainContent = React.useMemo(() => {
    switch (loginState) {
      case 'Login_SSOOFF':
        return (
          <UserPasswordForm
            initial={{
              UserName: '',
              Password: '',
              RememberMe: isMobile ? true : true,
              Recaptcha: '',
            }}
            onSubmit={handleSSOOFFSubmit}
            formRef={ssooffRef}
            showRememberMe={showRememberMe}
          />
        );
      case 'SSOON':
        return (
          <SelectLoginType
            onSSO_ON_Submit={handleSSO_ON_Submit}
            onSSO_OFF_Submit={handleSSO_OFF_Submit}
          />
        );
      case 'Login_SSOON':
        return (
          <UserPasswordForm
            initial={{
              UserName: '',
              Password: '',
              RememberMe: isMobile ? true : true,
              Recaptcha: '',
            }}
            onSubmit={handleSSOOFFSubmit}
            SSOSubmit={handleSSO_ON_Submit}
            formRef={ssooffRef}
            showRememberMe={showRememberMe}
          />
        );
      case 'TwoFactorAuthentication':
        return (
          <TwoFactorAuthenticationForm
            initial={{
              one: null,
              two: null,
              three: null,
              four: null,
              five: null,
              six: null,
            }}
            onSubmit={handle2FASubmit}
            formRef={twoFARef}
          />
        );
      default:
        return undefined;
    }
  }, [
    handle2FASubmit,
    handleSSOOFFSubmit,
    handleSSO_OFF_Submit,
    handleSSO_ON_Submit,
    isMobile,
    loginState,
    showRememberMe,
  ]);
  return [pageTitle, pageDescription, MainContent];
};
